import { Box, Drawer, Popover, Typography } from "@mui/material";
import { memo, useContext, useMemo } from "react";
import { SadFaceIcon } from "src/assets/icons/sad-face";
import { TagMetaData } from "src/config/store/services/media-api/types";
import { usePopover } from "src/modules/shared/hooks/use-popover/use-popover";
import { TagChip } from "../../tag-chip/tag-chip";
import { PlayerContext } from "../media-player";
import TagPopup from "../tag-popup/tag-popup";
import { classes } from "./tags-drawer.styles";

type TagsDrawerProps = {
  open: boolean;
  tags: TagMetaData[];
  drawerWidth: number;
};

const Tag = ({ tag }: { tag: Required<TagMetaData> }) => {
  const { anchorEl, open, close, isOpen } = usePopover();
  const { wrapperRef } = useContext(PlayerContext);

  return (
    <>
      <Box onClick={open} sx={classes.root}>
        <TagChip color={tag.data.color} icon={tag.data.icon} />
        <Typography sx={classes.tagName} variant="caption">
          {tag.data.name}
        </Typography>
      </Box>

      <Popover
        id={tag.id}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={close}
        container={wrapperRef.current}
        anchorOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <TagPopup close={close} tag={tag} />
      </Popover>
    </>
  );
};

const TagsDrawer: React.FC<TagsDrawerProps> = ({ open, tags, drawerWidth }) => {
  const filteredTags: Required<TagMetaData>[] = useMemo(
    () => tags.filter((x): x is Required<TagMetaData> => !!x.data),
    [tags]
  );

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          background: ({ palette }) => palette.dark.secondary,
          padding: ({ spacing }) => spacing(2),
        },
      }}
      variant="persistent"
      anchor="right"
      open={open}
      ModalProps={{
        container: document.querySelector("#testWrapper"),
        style: { position: "absolute" },
      }}
      PaperProps={{ style: { position: "absolute" } }}
      BackdropProps={{ style: { position: "absolute" } }}
    >
      <Box>
        {filteredTags.length === 0 && (
          <Box sx={classes.empty}>
            <SadFaceIcon />
            <Typography>No tags to show</Typography>
          </Box>
        )}

        {filteredTags.map((tag) => (
          <Tag key={tag.id} tag={tag} />
        ))}
      </Box>
    </Drawer>
  );
};

export default memo(TagsDrawer);
